import { useCallback, useMemo } from 'react';

import { useSort } from './useSort';
import { searchArray } from '../utilities';
import { useFiltered } from './useFiltered';
import { useListFetch } from './useListFetch';
import { usePaginated } from './usePaginated';
import type { UseListFetchConfig } from './useListFetch';
import type { UseListFetchResult } from './useListFetch';
import type { ListFetchRequestType } from './useListFetch';

type UseProcessedListConfig<
  T,
  FiltersType = Record<any, any>,
> = UseListFetchConfig<FiltersType> & {
  searchKeys?: (keyof T & string)[];
  searchFilterKey?: keyof NonNullable<FiltersType>;
  filterKeys?: (keyof T & string)[];
  withLocalSort?: boolean;
  withLocalPagination?: boolean;
};

type UseProcessedListResult<
  T extends {},
  FiltersType = Record<any, any>,
> = UseListFetchResult<T, FiltersType>;

const useSearched = <T extends {}, FiltersType = Record<any, any>>(
  nodes: T[],
  filters?: FiltersType,
  searchKeys?: (keyof T & string)[],
  searchFilterKey?: keyof NonNullable<FiltersType>
) => {
  return useMemo(() => {
    if (!searchKeys?.length) return nodes;

    const search = searchFilterKey ? filters?.[searchFilterKey] : '';
    return search && typeof search === 'string'
      ? searchArray(nodes, search, searchKeys)
      : nodes;
  }, [nodes, filters, searchFilterKey, searchKeys]);
};

export const useProcessedList = <T extends {}, FiltersType = Record<any, any>>(
  request: ListFetchRequestType<T, FiltersType>,
  config?: UseProcessedListConfig<T, FiltersType>
): UseProcessedListResult<T, FiltersType> => {
  const {
    nodes: data,
    filters,
    setFilters,
    ...rest
  } = useListFetch<T, FiltersType>(request, config);

  const { searchKeys, searchFilterKey, filterKeys } = config || {};
  const searched: T[] = useSearched(data, filters, searchKeys, searchFilterKey);
  const filtered = useFiltered({ nodes: searched, filters, keys: filterKeys });

  const { withLocalSort } = config || {};
  const { sorted: sortedNodes, ...sort } = useSort({
    nodes: withLocalSort ? filtered : [],
    defaultValue: config?.initialSort,
  });
  const sorted = withLocalSort ? sortedNodes : filtered;

  const { withLocalPagination } = config || {};
  const { nodes: paginatedNodes, ...paginated } = usePaginated({
    ...config,
    totalItems: sorted.length,
    nodes: withLocalPagination ? sorted : [],
  });
  const nodes = withLocalPagination ? paginatedNodes : sorted;

  const { setPage } = paginated;
  const handleSetFilters = useCallback<typeof setFilters>(
    (...params) => {
      setFilters(...params);
      withLocalPagination && setPage(1);
    },
    [withLocalPagination, setFilters, setPage]
  );

  return {
    nodes,

    filters,
    setFilters: handleSetFilters,

    ...rest,

    ...(withLocalSort ? sort : {}),
    ...(withLocalPagination ? paginated : {}),
  };
};
